import {
  CompanySettlementEntity, costStatisticsTypes, quantityStatisticsTypes,
  statisticsType,
  transportMethod,
  useCompanySettlementStatistics
} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Stack, Text, Title} from "@mantine/core";
import {IconChartDots3, IconTruck} from "@tabler/icons-react";
import {StatisticsDataTable} from "../../data-table/StatisticsDataTable/StatisticsDataTable";
import {toStatisticsDataGroups} from "../../data-table/StatisticsDataTable/useStatisticsDataTableColumns";
import {LineByTimeInsight} from "../../insights/ByTimeInsight/LineByTimeInsight";

export type CompanySettlementInsightsProps = {
  companySettlement: CompanySettlementEntity
}

export const CompanySettlementInsights = (props: CompanySettlementInsightsProps) => {
  const {companySettlement} = props;

  const {
    statistics,
    settlement,
    isLoading,
  } = useCompanySettlementStatistics({companySettlement})

  return (
    <Grid>
      {companySettlement.method === transportMethod.trucking &&
        <>
          <Grid.Col span={12}>
            <Divider my="sm"/>
            <Card shadow="sm" padding="lg" mb="lg" radius="md">
              <Stack>
                <Title order={3}>
                  Trucking Settlement
                </Title>
                <StatisticsDataTable
                  withBorder
                  scrollAreaProps={{
                    offsetScrollbars: true
                  }}
                  method={transportMethod.trucking}
                  types={[
                    statisticsType.quantity,
                    statisticsType.netQuantity,
                    statisticsType.billableQuantity,
                    statisticsType.billableTime,
                    statisticsType.demurrageTimeCost,
                    statisticsType.ticketRejectLoadCost,
                    statisticsType.fuelSurchargeCost,
                    statisticsType.timeCost,
                    statisticsType.loadCost,
                    statisticsType.freightCost,
                    statisticsType.cost
                  ]}
                  category={settlement?.activityPeriodCategory}
                  records={statistics?.truckCompanyStats}
                  recordsPerPageOptions={[10]}
                  fetching={isLoading}
                  hideExpansion={true}
                  showTotals={true}/>
              </Stack>
            </Card>
          </Grid.Col>
          {(settlement && settlement.activityPeriodFromDttm) &&
            <>
              <Grid.Col span={6}>
                <LineByTimeInsight
                  id="settlement_cost_by_time"
                  title={"Cost By Time"}
                  fromDttm={settlement.activityPeriodFromDttm}
                  toDttm={settlement.activityPeriodToDttm}
                  statisticsTypes={costStatisticsTypes}
                  data={statistics?.truckCompanyStats}
                  methods={transportMethod.trucking}
                  category={settlement.activityPeriodCategory}/>
              </Grid.Col>
              <Grid.Col span={6}>
                <LineByTimeInsight
                  id="settlement_quantity_by_time"
                  title={"Quantity By Time"}
                  fromDttm={settlement.activityPeriodFromDttm}
                  toDttm={settlement.activityPeriodToDttm}
                  statisticsTypes={quantityStatisticsTypes}
                  data={statistics?.truckCompanyStats}
                  methods={transportMethod.trucking}
                  category={settlement.activityPeriodCategory}/>
              </Grid.Col>
            </>
          }
        </>
      }
      {statistics?.disposalStats?.map((stats, index) => {
        return (
          <Grid.Col span={12} key={index}>
            <Divider my="sm"/>
            <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
              <Stack>
                <Title order={3}>
                  {stats.name + ": Disposal Settlement"}
                </Title>
                {companySettlement.method === transportMethod.trucking &&
                  <>
                    <Group mt="md" spacing="xs">
                      <IconTruck size={16}/>
                      <Text>Trucking</Text>
                    </Group>
                    <StatisticsDataTable
                      withBorder
                      withColumnBorders
                      scrollAreaProps={{
                        offsetScrollbars: true
                      }}
                      method={transportMethod.trucking}
                      types={[
                        statisticsType.quantity,
                        statisticsType.netQuantity,
                      ]}
                      category={settlement?.activityPeriodCategory}
                      records={Object.values(stats.truckCompanyMap)}
                      grouping={{
                        groupRetriever: (data) => data.siteMap,
                        groups: toStatisticsDataGroups(Object.values(stats.truckCompanyMap)?.map((subStats) => subStats.siteMap))
                      }}
                      recordsPerPageOptions={[10]}
                      fetching={isLoading}
                      hideExpansion={true}
                      showTotals={true}/>
                  </>
                }
                {companySettlement.method === transportMethod.pipeline &&
                  <>
                    <Group mt="md" spacing="xs">
                      <IconChartDots3 size={16}/>
                      <Text>Pipeline</Text>
                    </Group>
                    <StatisticsDataTable
                      withBorder
                      withColumnBorders
                      scrollAreaProps={{
                        offsetScrollbars: true
                      }}
                      method={transportMethod.pipeline}
                      types={[
                        statisticsType.quantity
                      ]}
                      category={settlement?.activityPeriodCategory}
                      records={Object.values(stats.pipelineMap)}
                      grouping={{
                        groupRetriever: (data) => data.siteMap,
                        groups: toStatisticsDataGroups(Object.values(stats.pipelineMap)?.map((subStats) => subStats.siteMap))
                      }}
                      recordsPerPageOptions={[10]}
                      fetching={isLoading}
                      hideExpansion={true}
                      showTotals={true}/>
                  </>
                }
              </Stack>
            </Card>
          </Grid.Col>
        )
      })}
    </Grid>
  );
};
